import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useVolunteerHours } from '@/hooks/use-volunteer-hours';
import { Clock, Calendar, Award, TrendingUp, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { VolunteerProfileCard } from '@/components/customer/VolunteerProfileCard';

export function VolunteerDashboard() {
  const { t, dir, language } = useLanguage();
  const { userData } = useAuth();
  const { hours, stats, loading } = useVolunteerHours(userData?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]" dir={dir}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8" dir={dir}>
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
          {t('dashboard.volunteer.dashboard.title') || 'Volunteer Dashboard'}
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          {t('dashboard.volunteer.dashboard.description') || 'Track your volunteer hours and contributions'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('dashboard.volunteer.stats.totalHours') || 'Total Hours'}
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalHours.toFixed(1) || 0}h</div>
            <p className="text-xs text-muted-foreground">
              {t('dashboard.volunteer.stats.allTime') || 'All time'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('dashboard.volunteer.stats.monthlyHours') || 'This Month'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.monthlyHours.toFixed(1) || 0}h</div>
            <p className="text-xs text-muted-foreground">
              {t('dashboard.volunteer.stats.currentMonth') || 'Current month'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('dashboard.volunteer.stats.yearlyHours') || 'This Year'}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.yearlyHours.toFixed(1) || 0}h</div>
            <p className="text-xs text-muted-foreground">
              {t('dashboard.volunteer.stats.currentYear') || 'Current year'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('dashboard.volunteer.stats.activities') || 'Activities'}
            </CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activitiesCount || 0}</div>
            <p className="text-xs text-muted-foreground">
              {t('dashboard.volunteer.stats.totalActivities') || 'Total activities'}
            </p>
          </CardContent>
        </Card>

        {/* Profile Card */}
        <VolunteerProfileCard />
      </div>

      {/* Recent Activities Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.volunteer.recentActivities') || 'Recent Volunteer Activities'}</CardTitle>
        </CardHeader>
        <CardContent>
          {hours.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium mb-2">
                {t('dashboard.volunteer.noActivities') || 'No volunteer hours recorded yet'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('dashboard.volunteer.noActivitiesDesc') ||
                  'Your volunteer hours will be recorded by administrators'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dashboard.volunteer.table.date') || 'Date'}</TableHead>
                    <TableHead>{t('dashboard.volunteer.table.activity') || 'Activity'}</TableHead>
                    <TableHead>{t('dashboard.volunteer.table.description') || 'Description'}</TableHead>
                    <TableHead>{t('dashboard.volunteer.table.hours') || 'Hours'}</TableHead>
                    <TableHead>{t('dashboard.volunteer.table.recordedBy') || 'Recorded By'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hours.map((hour) => (
                    <TableRow key={hour.id}>
                      <TableCell className="font-medium">{format(new Date(hour.date), 'PPP')}</TableCell>
                      <TableCell>{language === 'ar' ? hour.activityAr : hour.activityEn}</TableCell>
                      <TableCell className="max-w-[300px]">
                        <span className="line-clamp-2">
                          {language === 'ar' ? hour.descriptionAr : hour.descriptionEn || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{hour.hours}h</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{hour.addedByName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}